import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["type", "id", "ownerId"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import invariant from 'react-utils/invariant';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Form } from 'foundations-components/transitional/components/Form';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Box } from 'foundations-components/transitional/components/Box';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import { ViewSharingEditor } from '../../manageSharing/components/ViewSharingEditor';
import { useFetchSingleViewWithSettings } from '../../singleView/hooks/useFetchSingleViewWithSettings';
import { useCreateViewModalState } from '../hooks/useCreateViewModalState';
import { getLangStringsForStrategy } from '../utils/getLangStringsForStrategy';
import { useBETCanCurrentUserMakePublicView } from '../../manageSharing/useBETCanCurrentUserMakePublicView';
import { useCanCreatePrivateViewsOnly } from '../../utils/hooks/useCanCreatePrivateViewsOnly';
import { View as TrellisView } from 'foundations-components/transitional/components/View';
import { useHasViewSharingRedesign } from '../../manageSharing/hooks/useHasViewSharingRedesign';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
//aka - clone a view with draft changes

export const CREATE_VIEW_MODAL_STRATEGIES = {
  CREATE_NEW: 'CREATE_NEW',
  CLONE_EXISTING: 'CLONE_EXISTING',
  SAVE_AS_NEW: 'SAVE_AS_NEW'
};
export const CreateViewFromViewObjectModal = ({
  strategy,
  viewObject,
  onFailure,
  onSuccess,
  onClose,
  visualizationSettingsMap,
  scopeRequiredForSharedViews,
  customSharedViewPermissionErrorMessage
}) => {
  invariant(viewObject.id === undefined, 'You cannot declare an id on a view you are trying to clone - if you are creating a view, omit id');
  invariant(viewObject.intId === undefined, 'You cannot declare an intId on a view you are trying to clone - if you are creating a view, omit intId');
  invariant(viewObject.idLabel === undefined, 'You cannot declare an idLabel on a view you are trying to clone - if you are creating a view, omit idLabel');
  invariant(viewObject.type !== 'DEFAULT', 'viewObject.type was "DEFAULT" which is invalid - if you are creating a view, omit type');
  invariant(viewObject.objectTypeId !== undefined, 'viewObject.objectTypeId was not defined');
  invariant(viewObject.namespaceName !== undefined, 'viewObject.namespaceName was not defined');
  invariant(viewObject.filterGroups, 'viewObject.filterGroups was not defined');
  invariant(viewObject.quickFilters, 'viewObject.quickFilters was not defined');
  const {
    data: canOnlyCreatePrivateViews,
    loading: privateViewsOnlyLoading
  } = useCanCreatePrivateViewsOnly(scopeRequiredForSharedViews);
  const {
    isLoading,
    isError,
    textInputOnChange,
    handleSubmit,
    errorAffordance,
    errorValidationMessage,
    textInputValue,
    viewSharingUpdates,
    setViewSharingUpdates
  } = useCreateViewModalState({
    viewObject,
    createStrategy: strategy,
    onFailure,
    onSuccess,
    visualizationSettingsMap,
    canOnlyCreatePrivateViews
  });
  const {
    modalTitle,
    cancelButton,
    confirmButton
  } = getLangStringsForStrategy({
    strategy
  });
  const {
    data: hasBETPermission,
    loading: betPermissionLoading
  } = useBETCanCurrentUserMakePublicView();

  // User can create public views if they have BET permission AND are not restricted to private views only
  const hasPublicPermission = hasBETPermission && !canOnlyCreatePrivateViews;

  // User can create team views if they are not restricted to private views only
  const hasTeamPermission = !canOnlyCreatePrivateViews;
  const sharedViewPermissionLoading = betPermissionLoading || privateViewsOnlyLoading;
  const hasViewSharingRedesign = useHasViewSharingRedesign();
  const isAllLoading = isLoading || sharedViewPermissionLoading;
  const showSharingPreview = viewObject.namespaceName === 'NONE' && hasViewSharingRedesign;
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "create-view-modal",
    "data-test-loading": isLoading,
    width: showSharingPreview ? '900px' : undefined,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: modalTitle
        })
      })]
    }), /*#__PURE__*/_jsxs(Form, {
      children: [/*#__PURE__*/_jsxs(DialogBody, {
        children: [!isAllLoading && /*#__PURE__*/_jsx("div", {
          "data-test-id": "create-view-modal-loaded"
        }), isAllLoading && /*#__PURE__*/_jsx("div", {
          "data-test-id": "create-view-modal-loading"
        }), isError && /*#__PURE__*/_jsx("div", {
          "data-test-id": "create-view-modal-error"
        }), /*#__PURE__*/_jsx(FormControl, {
          "data-test-id": "view-name-input-form-control",
          error: Boolean(isError && !isLoading),
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.createViewModal.nameInput.label"
          }),
          required: true,
          validationMessage: !isLoading && errorValidationMessage,
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            children: [/*#__PURE__*/_jsx(Box, {
              shrink: 1,
              grow: 1,
              children: /*#__PURE__*/_jsx(TextInput, {
                "data-test-id": "create-single-view-modal-input",
                onChange: textInputOnChange,
                value: textInputValue,
                autoFocus: true
              })
            }), /*#__PURE__*/_jsx(Box, {
              shrink: 0,
              grow: 0,
              children: /*#__PURE__*/_jsx(LoadingSpinner, {
                failed: Boolean(isError),
                showResult: !isLoading
              })
            })]
          })
        }), /*#__PURE__*/_jsx(TrellisView, {
          marginBlockStart: "200",
          children: errorAffordance
        }), showSharingPreview && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(TrellisView, {
            marginBlock: "400",
            marginInline: "-600",
            borderBlockStartWidth: "100",
            borderBlockStartColor: "border-core-subtle",
            borderBlockStartStyle: "solid"
          }), /*#__PURE__*/_jsx(Text, {
            as: "h4",
            color: "text-core-default",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "viewsManagement.modals.createViewModal.access"
            })
          })]
        }), sharedViewPermissionLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
          "data-test-id": "permission-loading-spinner"
        }) : /*#__PURE__*/_jsx(ViewSharingEditor, {
          originalView: viewObject,
          viewUpdates: viewSharingUpdates,
          setViewUpdates: setViewSharingUpdates,
          hasPublicPermission: hasPublicPermission,
          hasTeamPermission: hasTeamPermission,
          customSharedViewPermissionErrorMessage: customSharedViewPermissionErrorMessage,
          showSharingPreview: showSharingPreview
        })]
      }), showSharingPreview && /*#__PURE__*/_jsx(TrellisView, {
        borderBlockStartWidth: "100",
        marginBlockStart: "400",
        borderBlockStartColor: "border-core-subtle",
        borderBlockStartStyle: "solid"
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(Button, {
          use: "primary",
          disabled: Boolean(isError || isLoading),
          onClick: handleSubmit,
          "data-test-id": "create-single-view-modal-submit-button",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: confirmButton
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "secondary",
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: cancelButton
          })
        })]
      })]
    })]
  });
};
export const CreateViewModal = ({
  strategy,
  objectTypeId,
  namespaceName,
  viewId,
  onSuccess,
  onFailure,
  onClose
}) => {
  const {
    data,
    loading
  } = useFetchSingleViewWithSettings({
    objectTypeId,
    namespaceName,
    id: viewId
  });
  const viewWithSettings = data === null || data === void 0 ? void 0 : data.singleViewWithSettingsById;
  if (loading || !(viewWithSettings !== null && viewWithSettings !== void 0 && viewWithSettings.view)) {
    return null;
  }
  const _viewWithSettings$vie = viewWithSettings.view,
    rawViewObject = _objectWithoutPropertiesLoose(_viewWithSettings$vie, _excluded);
  return /*#__PURE__*/_jsx(CreateViewFromViewObjectModal, {
    viewObject: Object.assign({}, rawViewObject, {
      type: 'STANDARD'
    }),
    strategy: strategy,
    onSuccess: onSuccess,
    onFailure: onFailure,
    onClose: onClose,
    visualizationSettingsMap: viewWithSettings.visualizationSettingsMap
  });
};